import React, { useState, useRef } from 'react';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { Button } from './button';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Speech to Text Button Component
 * Records audio and transcribes it to text using OpenAI Whisper
 * 
 * @param {function} onTranscription - Callback with transcribed text
 * @param {string} className - Additional CSS classes
 * @param {boolean} disabled - Disable the button
 */
export default function SpeechToTextButton({ onTranscription, className = '', disabled = false }) {
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
            mediaRecorderRef.current = mediaRecorder;
            chunksRef.current = [];

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunksRef.current.push(e.data);
                }
            };

            mediaRecorder.onstop = async () => {
                // Stop all tracks
                stream.getTracks().forEach(track => track.stop());
                
                // Process audio
                const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
                await transcribeAudio(audioBlob);
            };

            mediaRecorder.start();
            setIsRecording(true);
            toast.info('Recording started... Click again to stop');
        } catch (error) {
            console.error('Error starting recording:', error);
            if (error.name === 'NotAllowedError') {
                toast.error('Microphone access denied. Please allow microphone access.');
            } else {
                toast.error('Failed to start recording');
            }
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    };

    const transcribeAudio = async (audioBlob) => {
        setIsTranscribing(true);
        try {
            const formData = new FormData();
            formData.append('file', audioBlob, 'recording.webm');
            formData.append('language', 'en');

            const response = await axios.post(`${API}/speech-to-text`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success && response.data.transcription) {
                onTranscription(response.data.transcription);
                toast.success('Transcription complete!');
            } else {
                toast.error('No transcription received');
            }
        } catch (error) {
            console.error('Transcription error:', error);
            toast.error('Transcription failed. Please try again.');
        } finally {
            setIsTranscribing(false);
        }
    };

    const handleClick = () => {
        if (isRecording) {
            stopRecording();
        } else {
            startRecording();
        }
    };

    return (
        <Button
            type="button"
            variant={isRecording ? "destructive" : "outline"}
            size="icon"
            onClick={handleClick}
            disabled={disabled || isTranscribing}
            className={`flex-shrink-0 ${className}`}
            title={isRecording ? "Stop recording" : "Start voice input"}
            data-testid="speech-to-text-btn"
        >
            {isTranscribing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
            ) : isRecording ? (
                <MicOff className="w-4 h-4" />
            ) : (
                <Mic className="w-4 h-4" />
            )}
        </Button>
    );
}
